import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, RefreshCw, Loader2, CreditCard } from "lucide-react";
import { useSeo } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestsView() {
  useSeo("Request Submissions | SwiftTest Solutions", "Internal view of testing request submissions and payments.");
  const [code, setCode] = useState(sessionStorage.getItem("st_req_code") || "");
  const [authed, setAuthed] = useState(false);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async (c) => {
    setBusy(true);
    try {
      const headers = { "x-access-code": c };
      const [reqRes, payRes] = await Promise.all([
        axios.get(`${API}/testing-requests`, { headers }),
        axios.get(`${API}/payments`, { headers }),
      ]);
      setRequests(reqRes.data);
      setPayments(payRes.data);
      setAuthed(true);
      sessionStorage.setItem("st_req_code", c);
    } catch {
      setAuthed(false);
      toast.error("Invalid access code.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (code) load(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <div data-testid="requests-gate" className="flex min-h-[70vh] items-center justify-center bg-background px-5 pt-24">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(code);
          }}
          className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm"
        >
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
            <KeyRound className="h-6 w-6" />
          </span>
          <h1 className="mt-4 font-display text-xl font-bold text-slate-900">Submissions &amp; Payments</h1>
          <p className="mt-1.5 text-sm text-slate-500">Enter the access code to view activity.</p>
          <input
            data-testid="requests-code-input"
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Access code"
            className="mt-5 w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-500/40"
          />
          <button
            data-testid="requests-code-submit"
            type="submit"
            disabled={busy}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-teal-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-teal-800 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} View
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="requests-dashboard" className="min-h-screen bg-background px-5 pb-24 pt-28 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">
            Testing Requests <span className="text-teal-700">({requests.length})</span>
          </h1>
          <button
            data-testid="requests-refresh-button"
            onClick={() => load(code)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
        <div className="mt-8 space-y-4">
          {requests.length === 0 && (
            <p data-testid="requests-empty" className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              No requests yet.
            </p>
          )}
          {requests.map((r) => (
            <article
              key={r.id}
              data-testid={`request-item-${r.id}`}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-display text-lg font-bold text-slate-900">{r.name}</h2>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-bold ${
                    r.urgency === "urgent"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-teal-50 text-teal-800"
                  }`}
                >
                  {r.urgency === "urgent" ? "URGENT" : "Planned"}
                </span>
                <span className="text-xs text-slate-400">
                  {new Date(r.created_at).toLocaleString()}
                </span>
                {r.email_sent && (
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                    Email sent
                  </span>
                )}
              </div>
              <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
                <div><dt className="font-bold text-slate-500">Service</dt><dd className="text-slate-800">{r.service}</dd></div>
                <div><dt className="font-bold text-slate-500">Location</dt><dd className="text-slate-800">{r.city}</dd></div>
                <div><dt className="font-bold text-slate-500">Phone</dt><dd className="text-slate-800">{r.phone}</dd></div>
                <div><dt className="font-bold text-slate-500">Email</dt><dd className="text-slate-800">{r.email}</dd></div>
                {r.company && <div><dt className="font-bold text-slate-500">Company</dt><dd className="text-slate-800">{r.company}</dd></div>}
                {r.preferred_time && <div><dt className="font-bold text-slate-500">Preferred time</dt><dd className="text-slate-800">{r.preferred_time}</dd></div>}
                {r.notes && <div className="sm:col-span-2"><dt className="font-bold text-slate-500">Notes</dt><dd className="text-slate-800">{r.notes}</dd></div>}
              </dl>
            </article>
          ))}
        </div>

        <h2 className="mt-14 flex items-center gap-3 font-display text-2xl font-bold text-slate-900">
          <CreditCard className="h-6 w-6 text-teal-700" />
          Payments <span className="text-teal-700">({payments.length})</span>
        </h2>
        <div data-testid="payments-list" className="mt-6 space-y-4">
          {payments.length === 0 && (
            <p data-testid="payments-empty" className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              No payments yet.
            </p>
          )}
          {payments.map((p) => (
            <article
              key={p.id}
              data-testid={`payment-item-${p.id}`}
              className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-display text-lg font-bold text-slate-900">
                    ${Number(p.amount).toFixed(2)}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ${
                      p.status === "COMPLETED"
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {p.status}
                  </span>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold capitalize text-slate-600">
                    {p.kind}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  {[p.service, p.city, p.note].filter(Boolean).join(" · ") || "—"}
                  {p.payer_email ? ` · ${p.payer_email}` : ""}
                </p>
              </div>
              <span className="text-xs text-slate-400">
                {new Date(p.created_at).toLocaleString()}
              </span>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
